/**
 * Audit log — wireado contra `/api/audit`.
 *
 * Solo admin. Lista append-only de eventos con hash chain visible.
 * Click en una row expande payload JSON. Botón "Verify chain" pega a
 * `/api/audit/verify` on-demand (costoso). "Exportar CSV" abre el endpoint
 * `/api/audit/export.csv` con los filtros actuales.
 */
import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Download, ShieldCheck, RefreshCw } from "lucide-react";
import {
  useAuditLog,
  useAuditChainVerify,
  buildAuditCsvUrl,
  type AuditFilters,
  type PanelAuditEntry,
} from "@/hooks/use-audit";
import { EmptyState } from "@/components/shared/empty-state";
import { CLIENTS, findClient } from "@/lib/clients";
import type { ClientSlug } from "@/types";
import { cn } from "@/lib/cn";

const ROLE_LABEL: Record<PanelAuditEntry["actorRole"], string> = {
  admin: "admin",
  operator: "operador",
  client: "cliente",
  external: "externo",
  system: "sistema",
};

const ROLE_TONE: Record<PanelAuditEntry["actorRole"], string> = {
  admin: "bg-state-human-bg text-magenta border-state-human-border",
  operator: "bg-info-bg text-info-text border-info-border",
  client: "bg-state-bot-bg text-state-bot-text border-state-bot-border",
  external: "bg-surface-2 text-text-secondary border-default",
  system: "bg-state-escalated-bg text-state-escalated-text border-state-escalated-border",
};

function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "ahora";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "hace segundos";
  if (min < 60) return `hace ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  return `hace ${d}d`;
}

function short(hash: string | null | undefined, n = 8): string {
  if (!hash) return "—";
  return hash.length > n ? hash.slice(0, n) : hash;
}

export function AuditPage() {
  const [filters, setFilters] = useState<AuditFilters>({ limit: 100 });
  const [verifyEnabled, setVerifyEnabled] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const auditQ = useAuditLog(filters);
  const entries = useMemo(
    () => auditQ.data?.pages.flatMap((p) => p.items) ?? [],
    [auditQ.data],
  );
  const isLoading = auditQ.isLoading;
  const refetch = auditQ.refetch;
  const { data: verifyResult, isFetching: verifying, refetch: refetchVerify } =
    useAuditChainVerify(verifyEnabled);

  function handleVerify() {
    if (!verifyEnabled) setVerifyEnabled(true);
    refetchVerify();
  }

  function handleExport() {
    window.open(buildAuditCsvUrl(filters), "_blank");
  }

  return (
    <div className="min-h-full">
      {/* Header sticky */}
      <header
        className="px-7 pt-5 pb-4 border-b border-border-subtle sticky top-0 z-20"
        style={{
          background: "var(--surface-overlay)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-text-primary">
              Audit log
            </h1>
            <p className="mt-1 text-sm text-text-muted">
              Cadena verificable de eventos · sólo admin
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => refetch()}
              className="h-9 px-3 rounded-lg text-xs font-medium font-sans inline-flex items-center gap-1.5 border border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors"
              title="Refrescar"
            >
              <RefreshCw size={13} strokeWidth={1.75} />
              Refrescar
            </button>
            <button
              onClick={handleVerify}
              disabled={verifying}
              className={cn(
                "h-9 px-3.5 rounded-lg text-sm font-medium font-sans",
                "inline-flex items-center gap-2",
                "border border-default text-text-primary hover:bg-surface-2 transition-colors",
                "disabled:opacity-50",
              )}
            >
              <ShieldCheck size={14} strokeWidth={1.75} />
              {verifying ? "Verificando…" : "Verificar cadena"}
            </button>
            <button
              onClick={handleExport}
              className="h-9 px-3.5 rounded-lg text-sm font-medium font-sans inline-flex items-center gap-2 border border-magenta text-magenta bg-transparent hover:bg-magenta-soft transition-colors"
            >
              <Download size={14} strokeWidth={1.75} />
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Verify result banner */}
        {verifyResult && (
          <div
            role="status"
            className={cn(
              "mt-4 flex items-start gap-2.5 rounded-xl border px-4 py-3",
              verifyResult.valid
                ? "bg-success-bg border-success-border text-success-text"
                : "bg-error-bg border-error-border text-error-text",
            )}
          >
            <ShieldCheck size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
            <div className="text-sm">
              {verifyResult.valid ? (
                <>
                  Cadena íntegra ·{" "}
                  <span className="font-mono tabular-nums">{verifyResult.totalChecked}</span>{" "}
                  entradas verificadas.
                </>
              ) : (
                <>
                  Cadena rota en id{" "}
                  <span className="font-mono">{verifyResult.brokenAtId ?? "?"}</span> ·{" "}
                  {verifyResult.brokenReason ?? "razón desconocida"}.
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Body */}
      <div className="px-7 py-6">
        {/* Filtros */}
        <Filters value={filters} onChange={setFilters} />

        {/* Tabla */}
        <div className="mt-4 card-surface overflow-hidden">
          {isLoading ? (
            <div className="px-6 py-12 text-sm text-text-muted text-center">
              Cargando registros…
            </div>
          ) : !entries?.length ? (
            <EmptyState
              title="Sin eventos para los filtros aplicados"
              description="Probá quitar filtros o ampliar el rango de fechas."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-2 border-b border-subtle">
                  <tr className="text-left text-xs uppercase tracking-wider text-text-muted">
                    <th className="px-4 py-2.5 font-medium w-8" />
                    <th className="px-4 py-2.5 font-medium">ID</th>
                    <th className="px-4 py-2.5 font-medium">Acción</th>
                    <th className="px-4 py-2.5 font-medium">Recurso</th>
                    <th className="px-4 py-2.5 font-medium">Actor</th>
                    <th className="px-4 py-2.5 font-medium">Cliente</th>
                    <th className="px-4 py-2.5 font-medium">Hash</th>
                    <th className="px-4 py-2.5 font-medium text-right">Cuándo</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((row) => (
                    <AuditRow
                      key={row.id}
                      row={row}
                      expanded={expandedId === row.id}
                      onToggle={() =>
                        setExpandedId(expandedId === row.id ? null : row.id)
                      }
                    />
                  ))}
                </tbody>
              </table>
              {auditQ.hasNextPage && (
                <div className="px-4 py-3 border-t border-subtle flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => auditQ.fetchNextPage()}
                    disabled={auditQ.isFetchingNextPage}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-default text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors disabled:opacity-50 font-sans text-sm"
                  >
                    {auditQ.isFetchingNextPage ? "Cargando…" : "Cargar más"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditRow({
  row,
  expanded,
  onToggle,
}: {
  row: PanelAuditEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const client = row.clientSlug ? findClient(row.clientSlug) : null;
  return (
    <>
      <tr
        className={cn(
          "border-b border-subtle hover:bg-surface-2 cursor-pointer transition-colors",
          expanded && "bg-surface-2",
        )}
        onClick={onToggle}
      >
        <td className="px-4 py-2.5 text-text-muted">
          {expanded ? (
            <ChevronDown size={14} strokeWidth={1.75} />
          ) : (
            <ChevronRight size={14} strokeWidth={1.75} />
          )}
        </td>
        <td className="px-4 py-2.5 font-mono tabular-nums text-xs text-text-secondary">
          {row.id}
        </td>
        <td className="px-4 py-2.5 font-sans text-text-primary">{row.action}</td>
        <td className="px-4 py-2.5 font-sans text-xs text-text-secondary">
          <span className="text-text-muted">{row.resourceType}</span>
          <span className="text-text-disabled">/</span>
          <span className="font-mono">{short(row.resourceId, 12)}</span>
        </td>
        <td className="px-4 py-2.5">
          <span className="inline-flex items-center gap-1.5">
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-sans font-medium",
                ROLE_TONE[row.actorRole],
              )}
            >
              {ROLE_LABEL[row.actorRole]}
            </span>
            <span className="font-sans text-xs text-text-secondary truncate max-w-[180px]">
              {row.actorId}
            </span>
          </span>
        </td>
        <td className="px-4 py-2.5">
          {client ? (
            <span className="inline-flex items-center gap-1.5 font-sans text-xs text-text-secondary">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: client.accent }}
              />
              {client.label}
            </span>
          ) : (
            <span className="text-text-disabled text-xs">—</span>
          )}
        </td>
        <td className="px-4 py-2.5 font-mono text-xs text-text-secondary" title={row.hash}>
          {short(row.hash)}
        </td>
        <td
          className="px-4 py-2.5 text-right font-mono tabular-nums text-xs text-text-muted"
          title={row.createdAt}
        >
          {formatRelativeTime(row.createdAt)}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-surface-1 border-b border-subtle">
          <td colSpan={8} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <div className="section-label mb-2">Hash chain</div>
                <div className="font-mono text-text-secondary break-all">
                  <div>
                    <span className="text-text-muted">prev: </span>
                    {row.prevHash ?? "—"}
                  </div>
                  <div>
                    <span className="text-text-muted">hash: </span>
                    <span className="text-magenta">{row.hash}</span>
                  </div>
                </div>
              </div>
              <div>
                <div className="section-label mb-2">Metadata</div>
                <div className="font-mono text-text-secondary">
                  <div>
                    <span className="text-text-muted">actor: </span>
                    {row.actorId}
                  </div>
                  <div>
                    <span className="text-text-muted">creado: </span>
                    {row.createdAt}
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="section-label mb-2">Payload</div>
                <pre className="bg-surface-2 rounded-lg p-3 overflow-x-auto text-xs font-mono text-text-primary border border-subtle">
                  {JSON.stringify(row.payload, null, 2)}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Filters({
  value,
  onChange,
}: {
  value: AuditFilters;
  onChange: (next: AuditFilters) => void;
}) {
  return (
    <div className="card-surface p-3 flex flex-wrap items-center gap-2">
      <select
        value={value.clientSlug ?? ""}
        onChange={(e) =>
          onChange({
            ...value,
            clientSlug: (e.target.value || undefined) as ClientSlug | undefined,
          })
        }
        className="h-8 px-2 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary focus:border-magenta focus:outline-none w-40"
        aria-label="Cliente"
      >
        <option value="">Todos los clientes</option>
        {CLIENTS.map((c) => (
          <option key={c.slug} value={c.slug}>
            {c.fullName}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Acción (ej: human.takeover)"
        value={value.action ?? ""}
        onChange={(e) =>
          onChange({ ...value, action: e.target.value || undefined })
        }
        className="h-8 px-3 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none w-56"
      />
      <input
        type="text"
        placeholder="Resource type"
        value={value.resourceType ?? ""}
        onChange={(e) =>
          onChange({ ...value, resourceType: e.target.value || undefined })
        }
        className="h-8 px-3 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none w-44"
      />
      <input
        type="date"
        value={value.fromDate?.slice(0, 10) ?? ""}
        onChange={(e) =>
          onChange({
            ...value,
            fromDate: e.target.value
              ? new Date(e.target.value).toISOString()
              : undefined,
          })
        }
        className="h-8 px-3 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary focus:border-magenta focus:outline-none"
        aria-label="Desde"
      />
      <input
        type="date"
        value={value.toDate?.slice(0, 10) ?? ""}
        onChange={(e) =>
          onChange({
            ...value,
            toDate: e.target.value
              ? new Date(e.target.value).toISOString()
              : undefined,
          })
        }
        className="h-8 px-3 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary focus:border-magenta focus:outline-none"
        aria-label="Hasta"
      />
      {(value.clientSlug || value.action || value.resourceType || value.fromDate || value.toDate) && (
        <button
          onClick={() => onChange({ limit: value.limit })}
          className="h-8 px-2.5 text-xs font-sans rounded-md text-text-muted hover:text-text-primary transition-colors"
        >
          Limpiar
        </button>
      )}
    </div>
  );
}
